import type { BrandId } from './brands.ts';
import type { FinancePnlDataQualityStatus } from './internal-ops-finance.ts';

export type BusinessWindowPreset = 'today' | '7d' | '30d' | 'custom';
export type BusinessBrandFilter = 'all' | BrandId;
export type BusinessMetricUnit = 'count' | 'usd_cents' | 'usd_micros' | 'cny_cents' | 'ratio' | 'ms';
export type BusinessRiskSeverity = 'critical' | 'warning' | 'info';

/**
 * The single business timezone (epic #997 / #1001). Every business
 * window boundary (the `today` presets) and every trend day bucket is
 * expressed in UTC+8 — the PnL convention from platform
 * `internal-ops-finance.ts` (`BUSINESS_TZ_OFFSET_MS`). UTC+8 is a fixed
 * offset (no DST), so `'UTC+8'` is both the label and the complete
 * conversion rule.
 */
export const BUSINESS_TIMEZONE = 'UTC+8';
export type BusinessTimezone = typeof BUSINESS_TIMEZONE;

/**
 * The UTC+8 offset in milliseconds. Single source for every business
 * window boundary computation — producers must import this (and the
 * day-start helpers below) instead of restating the arithmetic, so the
 * "three timezone implementations" failure mode (epic #997) cannot
 * re-grow copy by copy.
 */
export const BUSINESS_TZ_OFFSET_MS = 8 * 60 * 60 * 1000;

/**
 * UTC instant (epoch ms) of the UTC+8 wall-clock midnight containing
 * `nowMs` — always 16:00Z on the previous UTC calendar day. This is
 * the canonical `today` lower bound for business KPIs, revenue
 * windows, usage windows, and trend day buckets.
 */
export function businessDayStartUtcMs(nowMs: number): number {
  const shifted = new Date(nowMs + BUSINESS_TZ_OFFSET_MS);
  return (
    Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate(), 0, 0, 0, 0) -
    BUSINESS_TZ_OFFSET_MS
  );
}

/** `businessDayStartUtcMs` for callers working with `Date` values. */
export function businessDayStartUtc(now: Date): Date {
  return new Date(businessDayStartUtcMs(now.getTime()));
}

/**
 * Currency-conversion snapshot used to derive every `*CnyCents` twin of
 * a USD usage-revenue figure in a business response (epic #997 / #1001).
 *
 * Single conversion source: platform-api's
 * `exchange_rate_policy.cny_per_usd` (read via `GET
 * /internal/ops/pricing`) — the same operator display rate FinancePnl /
 * `usePricing` renders with. The server converts once; frontends must
 * render the provided CNY values instead of converting (or worse,
 * mixing currencies) on their own.
 *
 * `source` is `'policy'` when the ops-managed policy row exists and is
 * valid, `'fallback'` when platform has no usable policy row and the
 * deliberately-high `PRICING_FALLBACK_CNY_PER_USD` was applied (see
 * `internal-pricing.ts`).
 */
export interface BusinessFxRate {
  readonly pair: 'USD_CNY';
  readonly cnyPerUsd: number;
  readonly source: 'policy' | 'fallback';
}

/**
 * `'traffic'` joined in Epic #853/#857 when the business risk queue
 * switched to the unified ops-api risk aggregation module. For rows
 * produced by that module, `area` is exactly the unified
 * `OpsRiskDimension` (`supply | finance | traffic`); `'customers'` and
 * `'system'` remain in the union for wire back-compat.
 */
export type BusinessRiskArea = 'finance' | 'customers' | 'supply' | 'system' | 'traffic';

/**
 * Machine-readable id of one downstream slice a business page endpoint
 * fans out to (#1002). Each `/v1/ops/business/:page` endpoint fetches
 * ONLY the slices its page renders (the per-page lists live in
 * ops-api's `business-dashboard.ts` and are unit-tested); a failed
 * slice no longer 5xxes the page — it nulls the dependent sections and
 * appends a `BusinessSliceWarning` instead.
 */
export type BusinessSliceId =
  // SKU economics (#1006) slices.
  | 'currentSkus'
  | 'funnel'
  | 'gatewayHealth'
  | 'platformHealth'
  | 'billingAnomalies'
  | 'traffic'
  | 'pricing'
  // Cockpit slices (#1004): the finance PnL snapshots for the current +
  // previous envelope windows and the two prepaid-runway projections.
  | 'pnlCurrent'
  | 'pnlPrevious'
  | 'upstreamRunway'
  | 'customerRunway';

/**
 * Section-level degradation record (#1002). Emitted when a downstream
 * slice fetch fails: the page responds 200 with the dependent sections
 * set to `null` and one warning per failed slice, instead of failing
 * the whole page with a 5xx. `sections` names the top-level response
 * keys that were nulled because of this slice (empty for slices with a
 * built-in fallback, e.g. `pricing` degrading to the fallback fx rate).
 */
export interface BusinessSliceWarning {
  readonly slice: BusinessSliceId;
  /** HTTP status the downstream slice fetch resolved to. */
  readonly status: number;
  readonly message: string;
  readonly sections: readonly string[];
}

/**
 * Window envelope for every `/v1/ops/business/*` response.
 *
 * **Required to render** (epic #997 / #1001): any page that displays a
 * number from a business response must also render the envelope —
 * `from`/`to`/`previousFrom`/`previousTo`/`timezone` plus the sibling
 * top-level `generatedAt` — so an operator can always tell which
 * wall-clock period (and which comparison period) a figure covers.
 * The rendering itself lands with the cockpit pages (#1004); this
 * contract fixes the data obligation.
 *
 * Semantics: `[from, to)` half-open (matching the funnel COUNT windows
 * from #999); `previous*` is the same-length window immediately before
 * `from`. For `preset: 'today'` the window starts at the current UTC+8
 * wall-clock midnight (`timezone` names the convention).
 */
export interface BusinessWindowEnvelope {
  readonly preset: BusinessWindowPreset;
  readonly from: string;
  readonly to: string;
  readonly previousFrom: string;
  readonly previousTo: string;
  /** Always `'UTC+8'` — see `BUSINESS_TIMEZONE`. */
  readonly timezone: BusinessTimezone;
  readonly brand: BusinessBrandFilter;
}

export interface BusinessMetric {
  readonly value: number;
  readonly previousValue: number;
  readonly deltaPct: number | null;
  readonly unit: BusinessMetricUnit;
}

export interface BusinessRiskItem {
  readonly id: string;
  readonly severity: BusinessRiskSeverity;
  readonly area: BusinessRiskArea;
  readonly title: string;
  readonly detail: string;
  readonly targetLabel: string;
  readonly href: string;
  readonly occurredAt: string | null;
}

export interface BusinessSupplyModelCoverage {
  readonly vendor: string;
  readonly channel: string;
  readonly model: string;
  readonly protocol: string;
  readonly coverage: number;
  readonly status: 'covered' | 'single_point' | 'unrouted';
}

export interface BusinessLowFundingUpstream {
  readonly upstreamId: string;
  readonly upstreamName: string;
  readonly remainingUsdCents: number;
  readonly capacityUsdCents: number;
  readonly pctRemaining: number;
}

// ─── SKU unit economics page (epic #997 P1.4, #1006) ─────────────────

/**
 * One row of the SKU unit-economics table (`GET
 * /v1/ops/business/sku-economics`). Derived from the platform
 * `/internal/ops/revenue/by-sku` aggregate (`currentSkus` slice) joined
 * with the SKU coverage matrix by canonical `namespace/model` name.
 *
 * `skuId === null` is the **legacy-path bucket** — usage rows charged
 * through the pre-#904 channel path (all rows written before
 * 2026-07-07 plus current legacy-key traffic). The UI labels it
 * explicitly; it is never dropped, so table sums stay reconcilable
 * with the overview totals (#1006 AC 1).
 *
 * `requestShare` = row `usageCount` / window total usage count
 * (fraction in [0, 1]; 0 when the window is empty).
 *
 * `coverage`/`coverageStatus` are null for the legacy bucket and for
 * SKUs missing from the priced-coverage matrix (deleted or unpriced),
 * or when a health slice failed (section degradation keeps the
 * economics columns renderable).
 */
export interface BusinessSkuEconomicsRow {
  readonly skuId: string | null;
  readonly skuName: string | null;
  readonly usageCount: number;
  readonly requestShare: number;
  readonly usdChargedMicros: number;
  readonly usdChargedCents: number;
  readonly cnyChargedCents: number;
  readonly cnyUpstreamCostCents: number;
  readonly usdUpstreamCostCents: number;
  readonly l2ProfitCnyCents: number;
  readonly l2ProfitUsdCents: number;
  readonly l2MarginPct: number;
  readonly coverage: number | null;
  readonly coverageStatus: 'covered' | 'single_point' | 'unrouted' | null;
}

/**
 * Window totals across every `skus` row (legacy bucket included) —
 * passed through from the platform by-SKU aggregate, which computes
 * them with the same filters as `/internal/ops/revenue/summary`, so
 * they reconcile with the overview/cockpit usage totals for the same
 * window+brand (#1006 AC 1).
 */
export interface BusinessSkuEconomicsTotals {
  readonly usageCount: number;
  readonly usdChargedMicros: number;
  readonly usdChargedCents: number;
  readonly cnyChargedCents: number;
  readonly cnyUpstreamCostCents: number;
  readonly l2ProfitCnyCents: number;
  readonly l2MarginPct: number;
}

/**
 * Coverage matrix row decorated with the marketplace SKU id so matrix
 * rows can link to `/market/:skuId` (#1006 AC 2, moved from
 * `/supply/market/:skuId` in #1336). `skuId` is
 * joined by canonical `channel/model` name from the by-SKU catalog;
 * null when the join misses (row stays non-clickable) or when the
 * `currentSkus` slice failed.
 */
export interface BusinessSkuCoverageMatrixRow extends BusinessSupplyModelCoverage {
  readonly skuId: string | null;
}

/**
 * Low-funding upstream row with burn-based runway. `runwayDays` is the
 * estimated days until depletion at recent burn — sourced from the
 * platform upstream-runway endpoints (#1004); null while that slice is
 * not yet wired or when burn is unknown (renders as pending, never 0).
 */
export interface BusinessLowFundingRunwayRow extends BusinessLowFundingUpstream {
  readonly runwayDays: number | null;
}

/**
 * `GET /v1/ops/business/sku-economics` — the money-aware supply page
 * (#1006): per-SKU margin/revenue/cost/request-share plus the existing
 * coverage matrix and low-funding panel. Section nullability follows
 * the #1002 convention (null section ⇔ a `warnings` entry names it).
 */
export interface BusinessSkuEconomicsResponse {
  readonly window: BusinessWindowEnvelope;
  readonly generatedAt: string;
  /** Slice-failure degradation records (#1002). Always emitted; empty
   * on the happy path. */
  readonly warnings?: readonly BusinessSliceWarning[];
  readonly totals: BusinessSkuEconomicsTotals | null;
  readonly skus: readonly BusinessSkuEconomicsRow[] | null;
  readonly coverage: {
    readonly pricedSkuCount: number;
    readonly unroutedSkuCount: number;
    readonly singlePointSkuCount: number;
    readonly lowFundingUpstreamCount: number;
    readonly unhealthyUpstreamCount: number;
  } | null;
  readonly matrix: readonly BusinessSkuCoverageMatrixRow[] | null;
  readonly lowFunding: readonly BusinessLowFundingRunwayRow[] | null;
}

// ─── Morning cockpit (#1004) ─────────────────────────────────────────

/**
 * One PnL window projected for the cockpit margin section. Every figure
 * is copied verbatim from platform-api `/internal/ops/finance/pnl`
 * (`FinancePnlSnapshot.operating`) for the same window — the cockpit
 * does NOT define a third profit term (epic #997 goal 1): `profit` is
 * exactly `realized_operating_profit_cny_cents`, so the cockpit margin
 * always equals the 真实盈亏 page for the same window.
 */
export interface BusinessCockpitMarginWindow {
  readonly from: string;
  readonly to: string;
  /** `operating.cash_funded_revenue_cny_cents`. */
  readonly revenueCnyCents: number;
  /** `operating.cash_funded_upstream_cost_cny_cents`. */
  readonly upstreamCostCnyCents: number;
  /** `operating.gifted_upstream_cost_cny_cents`. */
  readonly giftedCostCnyCents: number;
  /** `operating.realized_operating_profit_cny_cents`. */
  readonly profitCnyCents: number;
  /** `operating.margin_pct`. */
  readonly marginPct: number;
  /** `data_quality.status` — surfaced so an "unreliable" PnL cannot
   * render as a confident margin figure. */
  readonly dataQuality: FinancePnlDataQualityStatus;
}

/** Net cash in/out for one window — `FinancePnlSnapshot.cashflow`
 * verbatim (same finance source as the margin section). */
export interface BusinessCockpitCashWindow {
  readonly from: string;
  readonly to: string;
  readonly cashInCnyCents: number;
  readonly paymentFeeCnyCents: number;
  readonly upstreamCashOutCnyCents: number;
  readonly netCnyCents: number;
}

/** One upstream prepaid-runway row (platform `/internal/ops/runway/upstreams`
 * decorated with the gateway `upstream_names` map + a drill-down href). */
export interface BusinessCockpitRunwayRow {
  readonly upstreamId: string;
  /** Gateway display name; empty string when the gateway health slice
   * was unavailable or the upstream is soft-deleted. */
  readonly upstreamName: string;
  readonly remainingUsdCents: number;
  /** `remainingUsdCents` converted through the response `fx` (#1001). */
  readonly remainingCnyCents: number;
  readonly burnPerDayUsdCents: number;
  /** Days until prepaid depletion at the recent burn rate; `null` when
   * the burn window saw no attributed consumption. */
  readonly daysLeft: number | null;
  readonly href: string;
}

/**
 * One postpaid upstream account on the cockpit (#2756): what is owed to the
 * supplier and how much of the CNY credit line is left. Money is CNY — the
 * line and the ledger cost both are; the USD payable is at market rate.
 */
export interface BusinessCockpitPostpaidRow {
  readonly upstreamId: string;
  readonly upstreamName: string;
  readonly accountId: string;
  /**
   * NET payable = credit drawn: the account's unbatched upstream cost less
   * the unspent CNY in its live batches, floored at 0 — the same figure the
   * PnL sums as `upstream_postpaid_net_payable_cny_cents`. USD at the
   * payable's own market rate.
   */
  readonly netPayableCnyCents: number;
  readonly netPayableUsdCents: number;
  /** `null` = unlimited line. */
  readonly creditLimitCnyCents: number | null;
  readonly utilisationPct: number | null;
  readonly burnPerDayCnyCents: number;
  /** True when the account's position could not be read; figures are blank. */
  readonly unreadable?: boolean;
  /** Credit remaining ÷ daily burn; `null` when unlimited or idle. */
  readonly daysLeft: number | null;
  readonly href: string;
}

/** One customer balance-runway row (the "chase list"). */
export interface BusinessCockpitChaseRow {
  readonly accountId: string;
  readonly accountEmail: string;
  readonly remainingUsdCents: number;
  /** `remainingUsdCents` converted through the response `fx` (#1001). */
  readonly remainingCnyCents: number;
  readonly burnPerDayUsdCents: number;
  readonly daysLeft: number;
  readonly href: string;
}

/** Top open fires — the unified risk aggregation ranked critical-first. */
export interface BusinessCockpitFires {
  readonly openCount: number;
  readonly criticalCount: number;
  readonly warningCount: number;
  /** Severity-ranked (critical > warning > info, stable within a
   * severity), capped server-side — `openCount` is the true total. */
  readonly items: readonly BusinessRiskItem[];
}

/**
 * Compact growth funnel with real conversion rates (#999 data).
 *
 * Two DIFFERENT populations, deliberately kept as two labeled segments
 * (#999 handoff): `cohort` counts the window's registration cohort
 * (accounts created in the window, then verified/keyed subsets of that
 * cohort), while `activity` counts window activity across ALL accounts
 * (any account active/paying in the window, regardless of signup date).
 * Rendering them as one seamless funnel would imply keyed → active is a
 * subset relation, which it is not.
 */
export interface BusinessCockpitFunnel {
  readonly cohort: {
    readonly newAccounts: BusinessMetric;
    readonly verifiedAccounts: BusinessMetric;
    readonly keyedAccounts: BusinessMetric;
    /** `verified ÷ new`; `null` when the cohort is empty. */
    readonly verifiedRate: number | null;
    /** `keyed ÷ verified`; `null` when no verified accounts. */
    readonly keyedRate: number | null;
  };
  readonly activity: {
    readonly activeCustomers: BusinessMetric;
    readonly payingCustomers: BusinessMetric;
    /** `paying ÷ active`; `null` when no active customers. */
    readonly payingRate: number | null;
  };
}

/**
 * `GET /v1/ops/business/cockpit` — the morning cockpit page (#1004).
 *
 * `margin`/`cash` cover the envelope's current window plus the
 * comparison window (`previousFrom`..`previousTo`); with the default
 * `today` preset those are exactly "today so far" and "yesterday".
 * Section nullability follows the #1002 convention: `null` sections
 * mean a slice failure described in `warnings`.
 */
export interface BusinessCockpitResponse {
  readonly window: BusinessWindowEnvelope;
  readonly generatedAt: string;
  /** Conversion snapshot behind every `*CnyCents` twin (#1001). */
  readonly fx: BusinessFxRate;
  readonly warnings: readonly BusinessSliceWarning[];
  readonly margin: {
    readonly current: BusinessCockpitMarginWindow;
    readonly previous: BusinessCockpitMarginWindow;
  } | null;
  readonly cash: {
    readonly current: BusinessCockpitCashWindow;
    readonly previous: BusinessCockpitCashWindow;
  } | null;
  readonly upstreamRunway: {
    readonly burnWindowDays: number;
    readonly totalRemainingUsdCents: number;
    readonly totalRemainingCnyCents: number;
    /**
     * Capacity closed inside depleted funding batches across every
     * upstream — excluded from `totalRemaining*` because the allocator
     * cannot reach it, and disclosed here so the exclusion is visible
     * rather than silent (#1513). `0` on healthy books.
     */
    readonly totalStrandedUsdCents: number;
    readonly totalStrandedCnyCents: number;
    readonly rows: readonly BusinessCockpitRunwayRow[];
    /** Postpaid accounts (#2756). Absent when none is postpaid. */
    readonly postpaidRows?: readonly BusinessCockpitPostpaidRow[];
  } | null;
  readonly customerRunway: {
    readonly burnWindowDays: number;
    readonly rows: readonly BusinessCockpitChaseRow[];
  } | null;
  readonly fires: BusinessCockpitFires | null;
  readonly funnel: BusinessCockpitFunnel | null;
}
